import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';

export interface PendingInvitation {
  id: string;
  email: string;
  status: string;
  expires_at: string;
  created_at: string;
  branch_type: string;
  invited_by_user_id: string | null;
  invited_by_email: string | null;
}

export function usePendingPortalInvitations(vendorId: string, options?: { includeRevoked?: boolean }) {
  const { tenantId } = useAuthStore();
  const includeRevoked = options?.includeRevoked ?? false;
  return useQuery<PendingInvitation[]>({
    queryKey: ['portal-invitations', tenantId, vendorId, { includeRevoked }],
    queryFn: async () => {
      const params = includeRevoked ? '?includeRevoked=true' : '';
      const res = await apiClient.get<{ data: PendingInvitation[] }>(
        `/vendors/${vendorId}/portal-invitations${params}`,
      );
      return res.data.data ?? [];
    },
    enabled: !!tenantId && !!vendorId,
  });
}

export function useDisablePortalUser(vendorId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vendorAccountId: string) => {
      await apiClient.post(`/vendors/${vendorId}/portal-users/${vendorAccountId}/disable`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queryable', 'tenant_vendor_portal_users'] });
      toast.success('Portal user disabled');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to disable portal user');
    },
  });
}

export function useRemovePortalUser(vendorId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vendorAccountId: string) => {
      await apiClient.post(`/vendors/${vendorId}/portal-users/${vendorAccountId}/remove`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queryable', 'tenant_vendor_portal_users'] });
      toast.success('Portal user removed');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to remove portal user');
    },
  });
}

export interface InviteVendorContactPayload {
  email: string;
  firstName?: string;
  lastName?: string;
  roleTitle?: string;
}

export function useInviteVendorContact(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();
  return useMutation({
    mutationFn: async (payload: InviteVendorContactPayload) => {
      const { data } = await apiClient.post<{ data: unknown }>(
        `/vendors/${vendorId}/portal-contacts/invite`,
        payload,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-invitations', tenantId, vendorId] });
      toast.success('Invitation sent');
    },
    onError: (err: any) => {
      const code = err?.response?.data?.errors?.code;
      if (code === 'VENDOR_RELATIONSHIP_EXISTS') {
        toast.error('This vendor already has an active portal relationship');
        return;
      }
      if (code === 'INVITATION_PENDING') {
        toast.error('A pending invitation already exists for this email');
        return;
      }
      toast.error(err?.response?.data?.errors?.message ?? 'Failed to send invitation');
    },
  });
}

export function useCancelPortalInvitation(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();
  return useMutation({
    mutationFn: async ({ invitationId, reason }: { invitationId: string; reason?: string }) => {
      await apiClient.delete(`/vendors/${vendorId}/portal-invitations/${invitationId}`, {
        data: reason ? { reason } : undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-invitations', tenantId, vendorId] });
      toast.success('Invitation cancelled');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.message ?? 'Failed to cancel invitation';
      toast.error(msg);
      // Refetch so UI reflects the true state (may have drifted)
      queryClient.invalidateQueries({ queryKey: ['portal-invitations', tenantId, vendorId] });
    },
  });
}

export function useResendPortalInvitation(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();
  return useMutation({
    mutationFn: async (tokenId: string) => {
      await apiClient.post(`/vendors/${vendorId}/portal-invitations/${tokenId}/resend`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-invitations', tenantId, vendorId] });
      toast.success('Invitation resent');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to resend invitation');
    },
  });
}
